import { useState, useEffect, useCallback } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Inbox, Bot, RefreshCw, MessageSquareText, Send } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { avatarBg } from "@/lib/avatar";

const fallback = (name) => `https://ui-avatars.com/api/?name=${encodeURIComponent(name || "User")}&background=${avatarBg()}&color=fff`;

export default function AdminCompanionInbox({ password }) {
  const [convs, setConvs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [thread, setThread] = useState(null);
  const [threadOpen, setThreadOpen] = useState(false);
  const [impersonating, setImpersonating] = useState(false);

  const fetchInbox = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/companions/inbox?admin_password=${encodeURIComponent(password)}`);
      setConvs(r.data.conversations || []);
    } catch (e) {
      console.error("Companion inbox fetch failed:", e);
    } finally {
      setLoading(false);
    }
  }, [password]);

  useEffect(() => { fetchInbox(); }, [fetchInbox]);

  const openThread = async (conv) => {
    try {
      const r = await api.get(`/admin/companions/inbox/${conv.conversation_id}?admin_password=${encodeURIComponent(password)}`);
      setThread({ conv, messages: r.data.messages || [], aiIds: r.data.ai_ids || [] });
      setThreadOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load thread");
    }
  };

  const replyAs = async (conv) => {
    if (impersonating) return;
    setImpersonating(true);
    try {
      const r = await api.post(`/admin/companions/impersonate?admin_password=${encodeURIComponent(password)}`, { user_id: conv.companion.user_id });
      localStorage.setItem("token", r.data.access_token);
      localStorage.setItem("app_mode", conv.app === "dluxe" ? "dluxe" : "luxe");
      toast.success(`Now chatting as ${conv.companion.name} — log out to return`);
      setTimeout(() => { window.location.href = `/chat/${conv.conversation_id}`; }, 700);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impersonation failed");
      setImpersonating(false);
    }
  };

  return (
    <div className="glass-card p-6 mt-8" data-testid="companion-inbox-section">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-3">
          <Inbox className="w-5 h-5 text-rose-400" />
          <h2 className="text-xl font-semibold">Companion Inbox</h2>
        </div>
        <Button size="sm" variant="outline" className="border-white/10" onClick={fetchInbox} disabled={loading} data-testid="companion-inbox-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Every conversation between members and your AI companions, newest first. Open a thread to read it, or jump in and reply as the companion.
      </p>
      {convs.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="companion-inbox-empty">No companion conversations yet.</p>
      ) : (
        <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
          {convs.map((c) => (
            <div
              key={c.conversation_id}
              className="flex items-center gap-3 p-3 rounded-lg bg-black/20 border border-white/10 hover:border-white/20 transition-colors cursor-pointer"
              onClick={() => openThread(c)}
              data-testid={`companion-conv-${c.conversation_id}`}
            >
              <div className="flex -space-x-2 shrink-0">
                <img src={c.companion.photo || fallback(c.companion.name)} alt={c.companion.name} className="w-9 h-9 rounded-full object-cover ring-2 ring-rose-500/50" />
                <img src={c.member.photo || fallback(c.member.name)} alt={c.member.name} className="w-9 h-9 rounded-full object-cover ring-2 ring-black/60" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium flex items-center gap-1">
                    <Bot className="w-3.5 h-3.5 text-rose-400" /> {c.companion.name}
                  </span>
                  <span className="text-xs text-muted-foreground">↔</span>
                  <span className="text-sm font-medium">{c.member.name}</span>
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${c.app === "dluxe" ? "bg-rose-500/20 text-rose-400" : "bg-amber-500/20 text-amber-400"}`}>
                    {c.app === "dluxe" ? "DLuxe" : "LuxeMeet"}
                  </span>
                </div>
                {c.last_message && (
                  <p className="text-xs text-muted-foreground truncate mt-0.5">
                    {c.last_message.sender_id === c.companion.user_id ? `${c.companion.name}: ` : `${c.member.name}: `}
                    {c.last_message.content}
                  </p>
                )}
              </div>
              <div className="text-right shrink-0">
                <p className="text-[11px] text-muted-foreground">{c.last_message_at ? new Date(c.last_message_at).toLocaleString() : ""}</p>
                <p className="text-[11px] text-muted-foreground flex items-center justify-end gap-1 mt-0.5">
                  <MessageSquareText className="w-3 h-3" /> {c.message_count}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={threadOpen} onOpenChange={setThreadOpen}>
        <DialogContent className="bg-card border-white/10 max-w-lg" data-testid="companion-thread-dialog">
          {thread && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Bot className="w-5 h-5 text-rose-400" /> {thread.conv.companion.name} ↔ {thread.conv.member.name}
                </DialogTitle>
                <DialogDescription>
                  {thread.conv.member.email} · {thread.conv.app === "dluxe" ? "DLuxe" : "LuxeMeet"} inbox · {thread.messages.length} messages
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2 max-h-[45vh] overflow-y-auto pr-1" data-testid="companion-thread-messages">
                {thread.messages.map((m, i) => {
                  const isAi = thread.aiIds.includes(m.sender_id);
                  return (
                    <div key={m.message_id || i} className={`flex ${isAi ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm whitespace-pre-line ${isAi ? "bg-rose-500/15 border border-rose-500/25" : "bg-white/5 border border-white/10"}`}>
                        <p className={`text-[10px] mb-0.5 ${isAi ? "text-rose-300" : "text-muted-foreground"}`}>
                          {isAi ? thread.conv.companion.name : thread.conv.member.name} · {m.created_at ? new Date(m.created_at).toLocaleString() : ""}
                        </p>
                        {m.content}
                      </div>
                    </div>
                  );
                })}
              </div>
              <Button
                className="w-full bg-rose-600 hover:bg-rose-500"
                onClick={() => replyAs(thread.conv)}
                disabled={impersonating}
                data-testid="companion-reply-as-btn"
              >
                <Send className="w-4 h-4 mr-2" /> {impersonating ? "Switching…" : `Reply as ${thread.conv.companion.name}`}
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
